/**
 * Client-side Supabase queries for event bookmarks.
 * Follows the same pattern as db-corrections.ts.
 */

import type { SupabaseClient } from '@supabase/supabase-js';
import { getSupabase } from './supabase';

// bookmarks table is not in the generated almanak schema — reuse the shared
// browser singleton (avoids a duplicate GoTrueClient instance) but treat it as
// untyped so the unmapped `bookmarks` table is accessible.
function getUntypedSupabase(): SupabaseClient | null {
  return getSupabase() as unknown as SupabaseClient | null;
}

export interface Bookmark {
  id: string;
  user_id: string;
  event_slug: string;
  event_title: string;
  event_date_start: string | null;
  event_country: string | null;
  created_at: string;
}

export interface AddBookmarkData {
  user_id: string;
  event_slug: string;
  event_title: string;
  event_date_start?: string;
  event_country?: string;
}

export async function getBookmarks(userId: string): Promise<Bookmark[]> {
  const sb = getUntypedSupabase();
  if (!sb) return [];

  const { data, error } = await sb
    .from('bookmarks')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false });

  if (error) throw new Error(`Failed to fetch bookmarks: ${error.message}`);
  return (data ?? []) as Bookmark[];
}

export async function addBookmark(data: AddBookmarkData): Promise<Bookmark> {
  const sb = getUntypedSupabase();
  if (!sb) throw new Error('Supabase not configured');

  const { data: row, error } = await sb
    .from('bookmarks')
    .insert(data)
    .select()
    .single();

  if (error) throw new Error(`Failed to add bookmark: ${error.message}`);
  return row as Bookmark;
}

export async function removeBookmark(userId: string, eventSlug: string): Promise<void> {
  const sb = getUntypedSupabase();
  if (!sb) throw new Error('Supabase not configured');

  const { error } = await sb
    .from('bookmarks')
    .delete()
    .eq('user_id', userId)
    .eq('event_slug', eventSlug);

  if (error) throw new Error(`Failed to remove bookmark: ${error.message}`);
}
